import { SIZE } from '../constants/field-size';
import CryptoJS from 'crypto-js';
import { STATUS } from '../constants/form-status';
import dayjs from 'dayjs';

export const distinctObjectFromArray = (data, property) => {
  const uniqueValue = [...new Set(data.map(item => item[property]))];
  return uniqueValue;
}


export const fieldSizing = (fsize) => {
  // can do a db-table for this
  let fieldSize = {}

  switch (fsize?.toUpperCase()) {
    case SIZE.XXL:
      fieldSize = { lg: 12, md: 12, sm: 12, xs: 12 }
      break;
    case SIZE.XL:
      fieldSize = { lg: 6, md: 12, sm: 12, xs: 12 }
      break
    case SIZE.L:
    case SIZE.LG:
      fieldSize = { lg: 4, md: 12, sm: 12, xs: 12 }
      break;
    case SIZE.MD:
    case SIZE.M:
      fieldSize = { lg: 3, md: 6, sm: 12, xs: 12 }
      break;
    case SIZE.SM:
    case SIZE.S:
      fieldSize = { lg: 2, md: 4, sm: 6, xs: 12 }
      break;
    case SIZE.XS:
      fieldSize = { lg: 1, md: 3, sm: 6, xs: 6 }
      break;

    default:
      fieldSize = { lg: 6, md: 6, sm: 12, xs: 12 }
      break;
  }
  return fieldSize
}

export const filterByFormType = (state, formType) => {
  return state.filter(
    item =>
      // filter with form
        (item.Form === formType.form && item.Type === formType.type) 
      // ||
      //   (item.Form === "" && item.Type === formType.type)
      // (item.Type === formType.type)

  )
}

export const findByFormType = (state, formType) => {
  const find = state.find(item => item.Form === formType.form && item.Type === formType.type)
  return find;
}

export const capitalizeObjKeys = (obj) => {
  const newObj = {};
  for (let [key, value] of Object.entries(obj)) {
    const capitalizedKey = key.charAt(0).toUpperCase() + key.slice(1);
    newObj[capitalizedKey] = value;
  }
  return newObj;
};

export const lowerCaseStringFirstLetter = (string) => {
  return string.charAt(0).toLowerCase() + string.slice(1);
}
export const upperCaseStringFirstLetter = (string) => {
  return string.charAt(0).toUpperCase() + string.slice(1);
}

export const isEmptyObject = (obj) => {
  if (Object.keys(obj).length === 0) return true;
  return false;
}

export const sortObjectByKeys = (obj) => {
  // Get sorted entries
  const sortedEntries = Object.entries(obj).sort(([keyA], [keyB]) => keyA.localeCompare(keyB));
  // Convert sorted entries back to an object
  return Object.fromEntries(sortedEntries);
};

export const sortByFields = (fields) => {
  return fields
    ? fields.sort((a, b) => a.FieldOrder - b.FieldOrder)
    : [];
}

export const sortBySections = (sections) => {
  return sections
    ? sections.sort((a, b) => a.SecOrder - b.SecOrder)
    : [];
}

export const encryptData = (data) => {
  return CryptoJS.AES.encrypt(JSON.stringify(data), 'secretKey').toString();
}

export const decryptData = (data) => {
  return CryptoJS.AES.decrypt(data, 'secretKey').toString(CryptoJS.enc.Utf8);
}


export const evaluateFormula = (formula, data) => {
  // Extract keys and values from the data object
  const keys = Object.keys(data);
  const values = Object.values(data);
  
  const cleanedValues = values.map(value => {
      if (typeof value === 'string') {
        // Remove commas and convert to number
        const numericValue = parseFloat(value.replace(/,/g, ''));
        return isNaN(numericValue) ? value : numericValue;
      }
      return value;
  });

  try {
    // Create a function dynamically using new Function()
    const fn = new Function(...keys, `return ${formula};`);
    // Execute the function with the provided values
    const result = fn(...cleanedValues);
    // Convert result to string if needed (depending on requirements)
    return addCommasOnNumber(parseFloat(result.toString().replace(/,/g, '')).toFixed(2));
  } catch (error) {
    // // Handle invalid formula errors
    console.error('INVALID FORMULA: ', error);
    console.error('FORMULA: ', formula);
    throw formula;
  }
}


export const convertToWords = (strNum) => {
  const numberStr =  strNum?.toString().replace(/,/g, '') ;
  const [integerPart, decimalPart] = numberStr.split('.');

  const units = ['', 'one', 'two', 'three', 'four', 'five', 'six', 'seven', 'eight', 'nine'];
  const teens = ['ten', 'eleven', 'twelve', 'thirteen', 'fourteen', 'fifteen', 'sixteen', 'seventeen', 'eighteen', 'nineteen'];
  const tens = ['', '', 'twenty', 'thirty', 'forty', 'fifty', 'sixty', 'seventy', 'eighty', 'ninety'];
  const thousands = ['', 'thousand', 'million', 'billion', 'trillion'];

  // Function to convert a three-digit number to words
  function threeDigitToWords(numStr) {
    let num = parseInt(numStr, 10);
    if (num === 0) {
      return '';
    }

    let words = '';

    if (num >= 100) {
      words += units[Math.floor(num / 100)] + ' hundred ';
      num %= 100;
    }

    if (num >= 20) {
      words += tens[Math.floor(num / 10)] + ' ';
      num %= 10;
    } else if (num >= 10) {
      words += teens[num - 10] + ' ';
      num = 0; // Teens end the number
    }

    if (num > 0) {
      words += units[num] + ' ';
    }

    return words.trim();
  }

  // Convert integer part
  let words = '';
  let chunkCount = Math.ceil(integerPart.length / 3);

  for (let i = 0; i < chunkCount; i++) {
    let chunk = integerPart.slice(-3 * (i + 1), integerPart.length - 3 * i);
    let chunkWords = threeDigitToWords(chunk);

    if (chunkWords !== '') {
      chunkWords += ' ' + thousands[i] + ' ';
    }

    words = chunkWords + words;
  }

  words = words.trim().toUpperCase();

  // Convert decimal part if it exists
  if (decimalPart && (decimalPart !== "00" && decimalPart !== "0")) {
    let decimalWords = threeDigitToWords(decimalPart); // Convert the entire decimal part
    words += ' AND ' + decimalWords.toUpperCase() + ' CENTS';
  }

  return words;
}

export const addCommasOnNumber = (str) => {
  // Split the string into integer and decimal parts
  const [integerPart, decimalPart] = str.split('.');
  // Add commas to the integer part
  const formattedIntegerPart = integerPart.replace(/\B(?=(\d{3})+(?!\d))/g, ',');

  // Recombine integer and decimal parts
  return decimalPart ? `${formattedIntegerPart}.${decimalPart}` : formattedIntegerPart;
};


export const addSpaceBeforeCapsAndNumbers = (str)=> {
  let result = "";
  for (let i = 0; i < str.length; i++) {
      const char = str[i];
      if (/[A-Z0-9]/.test(char)) { // Check if character is capital letter or number
          result += " " + char;
      } else {
          result += char;
      }
  }
  
  return result;
}

export const getFileExtension = (fileName) => {
  const parts = fileName ? fileName.split('.') : "";
  return parts.length > 1 ? parts.pop() : '';
};

export const getFileICon = (fileName) => {
  const parts = fileName ? fileName.split('.') : "";
  const fileExtension = parts.length > 1 ? parts.pop() : '';

  switch (fileExtension) {
    case 'pdf':
      return process.env.PUBLIC_URL + '/assets/image/pdf_ico2.png'
    case 'doc':
    case 'docx':
      return process.env.PUBLIC_URL + '/assets/image/word_ico.png'
    case 'jpg':
    case 'jpeg':
    case 'png':
    case 'gif':
      return process.env.PUBLIC_URL + '/assets/image/img_ico.png'
    case 'xls':
    case 'xlsx':
      return `${process.env.PUBLIC_URL}/assets/image/excel_ico.png`;
    default:
      return process.env.PUBLIC_URL + '/assets/image/default_ico.png'
  }
};


export const createFormData = (file , newFileName , transactionID) => {
  // const encryptedFolderName = simpleSubstitutionCipher().encrypt(transactionID)
  // console.log(transactionID)
  // console.log(encryptedFolderName)

  const formData = new FormData();
  formData.append('userfile', file); // Append the file
  formData.append('newfilename', newFileName); // Append additional parameters
  formData.append('foldername', transactionID); // Append additional parameters

  return formData;
}

export const getExistingFileName = (fileName) => {
  const fileExtension = getFileExtension(fileName);
  return fileExtension ? fileName.replace(/\s+/g, '') : fileName.replace(/\s+/g, '') + '.pdf'; 
}

export const statusColor = (status) => {
  if(status === STATUS.SUBMITTED){
      return "#a1efb0"
  }
  else if(status === STATUS.INCOMPLETE){
      return "#ffe590"
  }
  return "#d9d7d0"
}

export const numberToOrdinalDay = (day) => {
  if (typeof day !== 'number' || day < 1) {
      return 'Invalid input';
  }

  const suffixes = ['th', 'st', 'nd', 'rd'];
  const value = day % 100;

  let suffix = suffixes[0]; // Default to 'th'
  if (value < 11 || value > 13) {
      suffix = suffixes[value % 10] || suffix;
  }

  return day + suffix;
}

export const getDetailedDates_Ordinal = (fieldName , fieldValue) => {
  const date_dd = dayjs(fieldValue).format('DD');
  const date_mm = dayjs(fieldValue).format('MMMM');
  const date_yy = dayjs(fieldValue).format('YY');

  return {
      [fieldName + "_DD"]: numberToOrdinalDay(parseInt(date_dd)) || '',
      [fieldName + "_MMMM"]: date_mm || '',
      [fieldName + "_YY"]: date_yy || '',
      [fieldName]: fieldValue
  }
}


export const getDetailedDates_Individuals = (fieldName, fieldValue) => {
  if(fieldValue){
      const date_DD = dayjs(fieldValue).format('DD').split('');
      const date_MM = dayjs(fieldValue).format('MM').split('');
      const date_YYYY = dayjs(fieldValue).format('YYYY').split('');

      return {
          [fieldName + "_DD1"]: date_DD[0] || '',
          [fieldName + "_DD2"]: date_DD[1] || '',
          [fieldName + "_MM1"]: date_MM[0] || '',
          [fieldName + "_MM2"]: date_MM[1] || '',
          [fieldName + "_YYYY1"]: date_YYYY[0] || '',
          [fieldName + "_YYYY2"]: date_YYYY[1] || '',
          [fieldName + "_YYYY3"]: date_YYYY[2] || '',
          [fieldName + "_YYYY4"]: date_YYYY[3] || ''
      }
  }

  return {}
}



export const simpleSubstitutionCipher = () => {
  const alphabet = 'abcdefghijklmnopqrstuvwxyz';
  const numbers = '0123456789';

  // Create a random mapping for letters and numbers
  const shuffledAlphabet = [...alphabet].sort(() => Math.random() - 0.5);
  const shuffledNumbers = [...numbers].sort(() => Math.random() - 0.5);

  const mapping = {};
  const reverseMapping = {};

  // Build mapping for lowercase letters
  alphabet.split('').forEach((char, index) => {
      mapping[char] = shuffledAlphabet[index];
      reverseMapping[shuffledAlphabet[index]] = char;
  });

  // Build mapping for uppercase letters
  alphabet.toUpperCase().split('').forEach((char, index) => {
      mapping[char] = shuffledAlphabet[index].toUpperCase();
      reverseMapping[shuffledAlphabet[index].toUpperCase()] = char;
  });

  // Build mapping for numbers
  numbers.split('').forEach((char, index) => {
      mapping[char] = shuffledNumbers[index];
      reverseMapping[shuffledNumbers[index]] = char;
  });

  function encrypt(text) {
      return text.split('').map(char => mapping[char] || char).join('');
  }

  function decrypt(encryptedText) {
      return encryptedText.split('').map(char => reverseMapping[char] || char).join('');
  }

  return { encrypt, decrypt };
}